// High Level Finance — trend fitting (pure math, no rendering).
//
// Ported unchanged from a sister dashboard's finance charts (its trend2.js):
// least-squares linear fit + outlier exclusion, the 95% confidence band,
// Classical Test Theory reliability, the optimal-outlier search and the
// segment geometry for a trend that is SOLID across the fitted periods and
// a dotted straight-line projection after them.

// Least-squares linear fit y = a + b*x over points [{ x, y }]. Exposes meanX,
// sxx, ssRes and residualVariance so the confidence band and CTT reliability
// reuse the exact same fit (including after outlier exclusion).
export function linearFit(points) {
  const pts = (points || []).filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number');
  const n = pts.length;
  if (n < 2) {
    const a = n === 1 ? pts[0].y : 0;
    return {
      a, b: 0, predict: () => a, residuals: pts.map(() => 0), r2: 0, usedPoints: n,
      meanX: n === 1 ? pts[0].x : 0, sxx: 0, ssRes: 0, residualVariance: 0,
    };
  }

  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  const mx = sx / n, my = sy / n;

  let sxx = 0, sxy = 0;
  for (const p of pts) { const dx = p.x - mx; sxx += dx * dx; sxy += dx * (p.y - my); }
  const b = sxx === 0 ? 0 : sxy / sxx;
  const a = my - b * mx;
  const predict = (x) => a + b * x;

  let ssRes = 0, ssTot = 0;
  const residuals = pts.map((p) => {
    const r = p.y - predict(p.x);
    ssRes += r * r;
    ssTot += (p.y - my) * (p.y - my);
    return r;
  });
  const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;
  const residualVariance = n > 2 ? ssRes / (n - 2) : 0; // regression dof = n - 2

  return { a, b, predict, residuals, r2, usedPoints: n, meanX: mx, sxx, ssRes, residualVariance };
}

// Two-sided 95% Student-t critical values t(0.975, df), df 1..30; df>30 → 1.96.
const T_975 = [
  NaN,
  12.706, 4.303, 3.182, 2.776, 2.571, 2.447, 2.365, 2.306, 2.262, 2.228,
  2.201, 2.179, 2.160, 2.145, 2.131, 2.120, 2.110, 2.101, 2.093, 2.086,
  2.080, 2.074, 2.069, 2.064, 2.060, 2.056, 2.052, 2.048, 2.045, 2.042,
];
export function tMultiplier(df) {
  if (df >= 1 && df <= 30) return T_975[df];
  if (df > 30) return 1.96;
  return T_975[1];
}

// Half-width of the 95% CONFIDENCE band for the regression MEAN at x:
//   half(x) = t(0.975, n-2) · s · sqrt(1/n + (x-x̄)²/Sxx),  s = sqrt(ssRes/(n-2)).
// Minimal where months cluster (x ≈ x̄); grows as x extrapolates → the fanning
// cone past Today. 0 when there aren't enough points (n < 3) or the fit is flat.
export function confidenceHalfWidth(fit, x) {
  if (!fit) return 0;
  const n = fit.usedPoints;
  if (n < 3) return 0;
  const s = Math.sqrt(fit.residualVariance);
  if (!(s > 0) || !(fit.sxx > 0)) return 0;
  const dx = x - fit.meanX;
  return tMultiplier(n - 2) * s * Math.sqrt(1 / n + (dx * dx) / fit.sxx);
}

// Classical Test Theory reliability with a FIXED denominator so it RISES as
// outliers are excluded: σ²_X = per-point variance of the FULL series (fixed);
// σ²_E = per-point residual variance of the current inlier fit.
//   Reliability = clamp(1 − σ²_E/σ²_X, 0, 1). At 0% excluded ≈ R². Needs ≥ 3
//   inliers, else null.
export function ctReliability(fullPoints, fit) {
  const pts = (fullPoints || []).filter((p) => p && typeof p.y === 'number');
  const M = pts.length;
  if (!fit || (fit.usedPoints || 0) < 3 || M < 1) return null;

  let sy = 0;
  for (const p of pts) sy += p.y;
  const meanFull = sy / M;
  let ssTotFull = 0;
  for (const p of pts) { const d = p.y - meanFull; ssTotFull += d * d; }
  const varX = ssTotFull / M;
  if (!(varX > 0)) return null;

  const varE = fit.ssRes / fit.usedPoints;
  return Math.max(0, Math.min(1, 1 - varE / varX));
}

// The OPTIMAL outlier % — the stepper value (0–50, step 5) whose 95% band is
// narrowest on average across the drawn range [first point .. evalTo].
//
// Reliability rises monotonically with exclusion (fixed denominator), so it
// can't define an optimum by itself. The band width already embodies the
// trade-off: excluding genuine outliers shrinks the residual spread s (band
// narrows), but past a point the small-sample penalties — t(n−2), 1/n and a
// shrinking Sxx — dominate and the band balloons. The bottom of that U-curve
// is the sweet spot. Degenerate candidates are skipped: fewer than 4 kept
// points, or a "perfect" zero-residual fit, would fake a zero-width band —
// certainty about almost nothing.
export function optimalOutlierPct(points, evalTo) {
  const pts = (points || []).filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number');
  const n = pts.length;
  if (n < 5) return 0;
  const x0 = pts[0].x;
  const xEnd = typeof evalTo === 'number' ? evalTo : pts[n - 1].x;

  let best = 0;
  let bestWidth = Infinity;
  let seenK = -1;
  for (let pct = 0; pct <= 50; pct += 5) {
    const k = Math.min(Math.round((pct / 100) * n), n - 2);
    if (k === seenK) continue; // same exclusion count as a smaller pct — keep the smaller label
    seenK = k;
    const { fit } = fitWithOutlierExclusion(pts, pct);
    if ((fit.usedPoints || 0) < 4) break; // exclusions only grow from here — all too thin
    const s = Math.sqrt(fit.residualVariance);
    if (!(s > 0) || !(fit.sxx > 0)) continue;
    let sum = 0;
    let m = 0;
    for (let x = x0; x <= xEnd + 1e-6; x += 0.5) { sum += confidenceHalfWidth(fit, x); m += 1; }
    if (!m) continue;
    const width = sum / m;
    if (width < bestWidth - 1e-9) { bestWidth = width; best = pct; }
  }
  return best;
}

// Fit, drop the top pct% of points by |residual|, then refit (always keeps ≥ 2).
// Returns { fit, included, excluded }.
export function fitWithOutlierExclusion(points, pct) {
  const pts = (points || []).filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number');
  if (pts.length < 2 || !pct || pct <= 0) {
    return { fit: linearFit(pts), included: pts, excluded: [] };
  }
  const first = linearFit(pts);
  const ranked = pts
    .map((p, i) => ({ p, ar: Math.abs(first.residuals[i]) }))
    .sort((u, v) => v.ar - u.ar); // largest |residual| first

  let k = Math.round((pct / 100) * pts.length);
  k = Math.max(0, Math.min(k, pts.length - 2)); // keep at least 2 points

  const excluded = ranked.slice(0, k).map((w) => w.p);
  const exclSet = new Set(excluded);
  const included = pts.filter((p) => !exclSet.has(p));
  return { fit: linearFit(included), included, excluded };
}

// Segment geometry for the trend line: SOLID across the fitted months, then a
// TRUE straight-line projection — the same fitted slope carried forward — drawn
// dotted from the last fitted month to the right edge.
//   points  : fit points [{x,y}] (x in the chart's fractional-index space)
//   pct     : outlier % to exclude
//   lastX   : x of the far right edge (last month centre) for the projection
// Returns { fit, sloped:[{x,y},{x,y}], projected:[{x,y},{x,y}]|null, fitEndX }.
export function trendSegments({ points, pct, lastX }) {
  const pts = (points || []).filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number');
  if (pts.length < 2) return null;

  const { fit, excluded } = fitWithOutlierExclusion(pts, pct);
  const x0 = pts[0].x;
  const fitEndX = pts[pts.length - 1].x; // last fitted month — projection starts here

  const sloped = [{ x: x0, y: fit.predict(x0) }, { x: fitEndX, y: fit.predict(fitEndX) }];
  const projected = lastX > fitEndX + 1e-9
    ? [{ x: fitEndX, y: fit.predict(fitEndX) }, { x: lastX, y: fit.predict(lastX) }]
    : null;

  // Central value at any x: the regression line itself, projected beyond the fit.
  const central = (x) => fit.predict(x);
  const reliability = ctReliability(pts, fit);

  return { fit, points: pts, excluded, sloped, projected, central, x0, fitEndX, reliability };
}
